import * as React from "react";
import { Paperclip } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils.js";

/**
 * Two shapes, same control:
 * - "link" (default): clickable text with a resting blue halo, not just on
 *   hover -- the fix for the reported "looks like plain text" defect.
 * - "button": a real button shape, matching Button's `outline` variant, for
 *   attachment controls that sit in a form row beside other buttons and are
 *   expected to look pressable.
 * The label carries the styling, so focus/disabled are keyed off the input it
 * wraps (has-[:focus-visible] / has-[:disabled]) rather than the label itself.
 */
const fileInputVariants = cva(
  "relative inline-flex max-w-full cursor-pointer select-none items-center gap-1.5 rounded-md has-[:disabled]:pointer-events-none has-[:disabled]:opacity-50",
  {
    variants: {
      variant: {
        link: "font-medium text-[var(--color-info-accent)] underline decoration-[var(--color-info-accent)]/40 decoration-dotted underline-offset-2 transition-shadow duration-200 shadow-[0_0_6px_1px_color-mix(in_srgb,var(--color-info-accent)_25%,transparent)] hover:decoration-solid hover:shadow-[0_0_10px_2px_color-mix(in_srgb,var(--color-info-accent)_45%,transparent)] has-[:focus-visible]:outline-none has-[:focus-visible]:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-info-accent)_50%,transparent)] has-[:disabled]:no-underline has-[:disabled]:shadow-none",
        button:
          "justify-center whitespace-nowrap border border-brand-500 bg-surface-0 font-semibold text-brand-700 shadow-sm transition-[color,background-color,border-color,box-shadow,transform] duration-150 ease-out hover:border-brand-600 hover:bg-brand-50 hover:shadow active:translate-y-px active:shadow-none has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand-500 has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-surface-0 has-[:disabled]:shadow-none",
      },
      size: {
        sm: "text-xs px-1 py-0.5 gap-1",
        default: "text-sm px-1.5 py-0.5",
      },
    },
    // The button shape needs real control heights, matching Button's sizes.
    compoundVariants: [
      { variant: "button", size: "sm", class: "h-8 px-3 py-0" },
      { variant: "button", size: "default", class: "h-9 px-4 py-0" },
    ],
    defaultVariants: { variant: "link", size: "default" },
  }
);

export interface FileInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "onChange" | "size">,
    VariantProps<typeof fileInputVariants> {
  /** Single-file mode (default): the currently-picked file, or null. Omit in multiple mode. */
  value?: File | null;
  /** Single-file mode: fires with the picked file, or null. */
  onChange?: (file: File | null) => void;
  /** Multiple mode (pass `multiple`): fires with every file picked in this dialog --
   * accumulate into your own list state, same shape every existing multi-file call site already used. */
  onFilesChange?: (files: File[]) => void;
  placeholder?: string;
}

export const fileInputDefaults = { placeholder: "Choose file", variant: "link", size: "default" } as const;

export const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  ({ className, value, onChange, onFilesChange, placeholder = fileInputDefaults.placeholder, variant, size, disabled, multiple, ...props }, ref) => {
    return (
      <label className={cn(fileInputVariants({ variant, size }), className)}>
        <Paperclip className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{value ? value.name : placeholder}</span>
        <input
          ref={ref}
          type="file"
          multiple={multiple}
          disabled={disabled}
          // Pinned inside the label (relative): a bare sr-only input takes its
          // static position against the page, and far down a long page that
          // stretches the document, so the whole window (sidebar included) scrolls.
          className="sr-only top-0 left-0"
          onChange={e => {
            const picked = Array.from(e.target.files ?? []);
            if (multiple) onFilesChange?.(picked);
            else onChange?.(picked[0] ?? null);
            e.target.value = ""; // lets re-picking the same file re-fire onChange, matches every call site's prior manual reset
          }}
          {...props}
        />
      </label>
    );
  }
);
FileInput.displayName = "FileInput";
